import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../../axiosConfig";
import { Widget as WidgetViewer } from "../../widget";
import WorkbookEmptyState from "./WorkbookEmptyState";
import WorkbookProgressChecklist from "./WorkbookProgressChecklist";
import WorkbookStepNavigation from "./WorkbookStepNavigation";
import useWorkbookChecklistProgress from "./useWorkbookChecklistProgress";
import {
  getWorkbookPageInterpretation,
  getWorkbookWidgets,
  resolveWorkbookOrganizationSurveyId,
} from "../organizationWorkbookApi";
import { getWorkbookRoutePrefix, isReadOnlyWorkbookRoute } from "./workbookRouting";
import { formatOrganizationSurveyTitle } from "../../../utils/organizationSurveyHelper";

const getChecklistWidgetId = (item) =>
  item?.linked_widget_id ?? item?.widget_id ?? null;

const pickChecklistItems = (interpretationPayload) =>
  interpretationPayload?.diagnostic_checklist ||
  interpretationPayload?.checklist ||
  [];

const isChecklistItemCompleted = (item, progress) =>
  Boolean(progress[item?.id] ?? item?.completed);

const WorkbookPracticeWorkspace = () => {
  const { t } = useTranslation();
  const { pageId, organizationId } = useParams();
  const location = useLocation();
  const routePrefix = getWorkbookRoutePrefix(location.pathname);
  const readOnly = isReadOnlyWorkbookRoute(location.pathname, location.state);
  const interpretationIdFromState = location.state?.interpretationId || null;
  const organizationSurveyIdFromState =
    location.state?.organizationSurveyId || null;

  const [loading, setLoading] = useState(true);
  const [organizationSurveyId, setOrganizationSurveyId] = useState(null);
  const [interpretationPayload, setInterpretationPayload] = useState(null);
  const [widgetsPayload, setWidgetsPayload] = useState(null);
  const [error, setError] = useState("");
  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [loadingWidget, setLoadingWidget] = useState(false);
  const [visibleChecklistItems, setVisibleChecklistItems] = useState([]);
  const [visibleChecklistSnapshotKey, setVisibleChecklistSnapshotKey] =
    useState(null);

  useEffect(() => {
    let active = true;

    const loadWorkspace = async () => {
      try {
        setLoading(true);
        setError("");
        setSelectedChecklistItem(null);
        setSelectedWidget(null);
        setVisibleChecklistItems([]);
        setVisibleChecklistSnapshotKey(null);

        const resolvedSurveyId = await resolveWorkbookOrganizationSurveyId({
          routeStateOrganizationSurveyId: organizationSurveyIdFromState,
          organizationId,
        });
        const nextInterpretationPayload = await getWorkbookPageInterpretation(
          resolvedSurveyId,
          pageId
        );
        const interpretationId =
          interpretationIdFromState ||
          nextInterpretationPayload?.interpretation_id ||
          nextInterpretationPayload?.interpretation?.id ||
          null;

        let nextWidgetsPayload = { checklist: [], widgets: [], attachments: [] };
        if (interpretationId) {
          nextWidgetsPayload = await getWorkbookWidgets(
            resolvedSurveyId,
            interpretationId
          );
        }

        if (!active) return;
        setOrganizationSurveyId(resolvedSurveyId);
        setInterpretationPayload(nextInterpretationPayload);
        setWidgetsPayload(nextWidgetsPayload);
      } catch (err) {
        if (!active) return;
        setError("Could not load the workbook.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadWorkspace();

    return () => {
      active = false;
    };
  }, [interpretationIdFromState, organizationId, organizationSurveyIdFromState, pageId]);

  const interpretation = interpretationPayload?.interpretation || null;
  const interpretationId =
    interpretationIdFromState ||
    interpretationPayload?.interpretation_id ||
    interpretation?.id ||
    null;
  const fallbackChecklistItems = useMemo(
    () =>
      widgetsPayload?.checklist?.length > 0
        ? widgetsPayload.checklist
        : pickChecklistItems(interpretation),
    [interpretation, widgetsPayload]
  );
  const widgets = useMemo(
    () => widgetsPayload?.widgets || [],
    [widgetsPayload]
  );
  const attachments = useMemo(
    () => widgetsPayload?.attachments || [],
    [widgetsPayload]
  );
  const surveyTitle = formatOrganizationSurveyTitle(interpretationPayload || widgetsPayload || {});

  const checklist = useWorkbookChecklistProgress({
    interpretationId,
    organizationSurveyId,
    fallbackItems: fallbackChecklistItems,
    readOnly,
  });

  const nextVisibleChecklistSnapshotKey =
    organizationSurveyId && interpretationId && pageId
      ? `${organizationSurveyId}:${interpretationId}:${pageId}`
      : null;

  useEffect(() => {
    if (
      loading ||
      !nextVisibleChecklistSnapshotKey ||
      !checklist.hasLoaded ||
      checklist.loading ||
      visibleChecklistSnapshotKey === nextVisibleChecklistSnapshotKey
    ) {
      return;
    }

    setVisibleChecklistItems(
      checklist.items.filter(
        (item) => !isChecklistItemCompleted(item, checklist.progress)
      )
    );
    setVisibleChecklistSnapshotKey(nextVisibleChecklistSnapshotKey);
  }, [
    checklist.hasLoaded,
    checklist.items,
    checklist.loading,
    checklist.progress,
    loading,
    nextVisibleChecklistSnapshotKey,
    visibleChecklistSnapshotKey,
  ]);

  const loadWidgetForItem = useCallback(
    async (item) => {
      setSelectedChecklistItem(item);
      const widgetId = getChecklistWidgetId(item);

      if (!widgetId) {
        setSelectedWidget(null);
        setLoadingWidget(false);
        return;
      }

      const preloadedWidget = widgets.find(
        (widget) => String(widget?.id) === String(widgetId)
      );
      if (preloadedWidget) {
        setSelectedWidget(preloadedWidget);
        setLoadingWidget(false);
        return;
      }

      try {
        setLoadingWidget(true);
        setSelectedWidget(null);
        const response = await axios.get(`/widgets/${widgetId}`, {
          params: { organization_survey_id: organizationSurveyId },
        });
        setSelectedWidget(response.data);
      } catch (err) {
        setSelectedWidget(null);
      } finally {
        setLoadingWidget(false);
      }
    },
    [organizationSurveyId, widgets]
  );

  useEffect(() => {
    if (loading || !organizationSurveyId) return;

    if (!checklist.hasLoaded) return;

    if (visibleChecklistItems.length > 0) {
      const currentItemStillExists = visibleChecklistItems.some(
        (item) => String(item.id) === String(selectedChecklistItem?.id)
      );
      if (currentItemStillExists) return;

      const firstWithWidget =
        visibleChecklistItems.find((item) => getChecklistWidgetId(item)) ||
        visibleChecklistItems[0];
      loadWidgetForItem(firstWithWidget);
      return;
    }

    if (checklist.items.length === 0 && !selectedWidget && widgets.length > 0) {
      setSelectedWidget(widgets[0]);
    }
  }, [
    checklist.hasLoaded,
    checklist.items,
    loadWidgetForItem,
    loading,
    organizationSurveyId,
    selectedChecklistItem,
    selectedWidget,
    visibleChecklistItems,
    widgets,
  ]);

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!interpretation && checklist.items.length === 0 && widgets.length === 0) {
    return (
      <VStack align="stretch" spacing={6}>
        <WorkbookStepNavigation
          pageId={pageId}
          organizationSurveyId={organizationSurveyId}
          interpretationId={interpretationId}
          currentStep="widgets"
          routePrefix={routePrefix}
          readOnly={readOnly}
        />
        <WorkbookEmptyState
          title={t("organization.workbook.no_interpretation_title", "Interpretation coming soon")}
          description={t(
            "organization.workbook.no_interpretation_description",
            "This page does not currently have interpretation content available."
          )}
        />
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <WorkbookStepNavigation
        pageId={pageId}
        organizationSurveyId={organizationSurveyId}
        interpretationId={interpretationId}
        currentStep="widgets"
        routePrefix={routePrefix}
        readOnly={readOnly}
      />

      {surveyTitle ? (
        <Text fontWeight="semibold">{surveyTitle}</Text>
      ) : null}

      {checklist.error ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {checklist.error}
        </Alert>
      ) : null}

      <Grid
        templateColumns={{ base: "1fr", lg: "minmax(280px, 360px) minmax(0, 1fr)" }}
        gap={6}
        alignItems="start"
      >
        <GridItem>
          <WorkbookProgressChecklist
            title={t(
              "organization.workbook.diagnostic_checklist_title",
              "Diagnostic Checklist"
            )}
            items={visibleChecklistItems}
            progress={checklist.progress}
            loading={checklist.loading || !checklist.hasLoaded}
            selectedItemId={selectedChecklistItem?.id}
            completedCount={checklist.completedCount}
            totalCount={checklist.totalCount}
            percentComplete={checklist.percentComplete}
            onSelect={loadWidgetForItem}
            onToggle={checklist.toggleItem}
            readOnly={readOnly}
            emptyMessage={
              checklist.items.length > 0
                ? t(
                    "organization.workbook.all_practices_complete",
                    "All diagnostic checklist items are complete. You can return to the Interpretation page to review the full checklist."
                  )
                : undefined
            }
          />
        </GridItem>

        <GridItem minW={0}>
          {loadingWidget ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : selectedWidget ? (
            <WidgetViewer
              widget={selectedWidget}
              attachments={attachments}
              organizationSurveyId={organizationSurveyId}
              mode="workbook"
            />
          ) : (
            <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200" bg="white">
              <CardBody>
                <Heading size="md">
                  {t(
                    "organization.workbook.no_linked_practice_title",
                    "No practice is linked to this checklist item."
                  )}
                </Heading>
                <Text color="gray.600" mt={2}>
                  {t(
                    "organization.workbook.no_linked_practice_description",
                    "Select another checklist item to view a practice."
                  )}
                </Text>
              </CardBody>
            </Card>
          )}
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default WorkbookPracticeWorkspace;
